import { motion } from 'motion/react';
import { AudioWaveform } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/kit';
import { api } from '@/lib/api';
import { choreName, m } from '@/lib/i18n';
import { freqLabel } from '@/lib/chore-text';
import { useAction } from '@/lib/queries';
import type { AppState } from '@/lib/types';

type Tip = AppState['rhythm'][number];

/** What the app noticed about a chore, in words. */
function noticed(tip: Tip) {
  const chore = choreName(tip);
  if (tip.reason === 'skipped') return m.rhythm_skipped({ chore, count: tip.count });
  if (tip.reason === 'late') return m.rhythm_late({ chore });
  return m.rhythm_early({ chore });
}

/**
 * Rhythm learning: "You skipped the fridge 3 times. Every 3 weeks instead of
 * every 2?" A suggestion card with two plain answers (Zocdoc's recommendation
 * cards); one tap changes the chore.
 */
export function RhythmCard({ tip }: { tip: Tip }) {
  const answer = useAction((freqDays: number | null) => api('POST', `/chores/${tip.choreId}/rhythm`, { freqDays }));
  const choose = (freqDays: number | null) =>
    answer.mutate(freqDays, { onSuccess: () => freqDays && toast.success(m.rhythm_changed({ chore: choreName(tip), freq: freqLabel(freqDays).toLowerCase() })) });
  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card grid gap-3 rounded-3xl border p-5">
      <div className="text-muted-foreground flex items-center gap-1.5 text-[13px] font-semibold tracking-wide uppercase">
        <AudioWaveform className="size-4" /> {m.rhythm_label()}
      </div>
      <div className="grid gap-1">
        <p className="text-muted-foreground text-[15px]">{noticed(tip)}</p>
        <p className="text-lg leading-snug font-semibold text-balance">{m.rhythm_question({ new: freqLabel(tip.suggested), old: freqLabel(tip.freqDays).toLowerCase() })}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="ghost" onClick={() => choose(null)} disabled={answer.isPending}>
          {answer.isPending && answer.variables === null && <Spinner />}
          {m.rhythm_keep()}
        </Button>
        <Button variant="secondary" onClick={() => choose(tip.suggested)} disabled={answer.isPending}>
          {answer.isPending && answer.variables !== null && <Spinner />}
          {m.rhythm_change()}
        </Button>
      </div>
    </motion.section>
  );
}
